import React from 'react';
import { AxiosError, AxiosResponse } from 'axios';
import { Column, Options, Action } from 'material-table';
import { Button, Grid, makeStyles, Tab, Tabs, Typography } from '@material-ui/core';

import { HistoryLocation } from '../enum/HistoryLocation';
import { HomeContext } from '../../Home/HomeContext';
import { AuthContext } from '../../Authentication/AuthContext';
import { NewsStatus } from '../../Classification/enum/NewsStatus';
import SpreadsheetService from '../../../Services/SpreadsheetService';
import { SpreadsheetModel, spreadsheetModelInitial } from '../../../Models/SpreadsheetModel';
import { PageLocation } from '../../../shared/enum/PageLocation';
import { TableEmpty } from '../../../shared/components/TableEmpty';
import { TableCommon, ITableCommonToolbar } from '../../../shared/components/TableCommon';
import { BasicConfirmDialog } from '../../../shared/components/BasicConfirmDialog';
import { theme } from '../../../theme';
import { TableLoading } from '../../../shared/components/TableLoading';
import { NewsOrigin } from '../../../shared/enum/NewsOrigin';
import { getTableColumn } from '../../../shared/operators';

export interface IHistoryClassificationProps {
    handlePageLocation: (location: PageLocation) => void;
}

const useStyles = makeStyles((theme) => ({
    root: {
        padding: theme.spacing(1.5, 4),
        width: '100%',
        maxWidth: '1800px'
    },
    title: {
        margin: '20px 0px 32px'
    },
    tabs: {
        justifyContent: 'space-between',
        display: 'flex'
    },
    addNewButtonGrid: {
        justifyItems: 'center'
    },
    emptyTable: {
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
        maxWidth: '1800px',
        padding: theme.spacing(0, 4)
    },
}));

export function HistoryClassification() {
    const classes = useStyles();

    const authContext = React.useContext(AuthContext);
    const homeContext = React.useContext(HomeContext);

    const [historyType, setHistoryType] = React.useState<HistoryLocation>(HistoryLocation.PENDING);
    const [spreadsheets, setSpreadsheets] = React.useState<SpreadsheetModel[]>([]);
    const [showConfirmDelete, setShowConfirmDelete] = React.useState(false);
    const [spreadsheetDelete, setSpreadsheetDelete] = React.useState<SpreadsheetModel>(spreadsheetModelInitial);

    const [loading, setLoading] = React.useState(true);

    React.useEffect(() => {
        setLoading(true);

        if (historyType == HistoryLocation.PENDING) {
            SpreadsheetService.getAllByStatus(authContext.login.user.id, NewsStatus.PENDING)
                .then((res: AxiosResponse) => { setSpreadsheetsData(res.data); })
                .catch((error: AxiosError) => { homeContext.showError('', error); });
        }
        else if (historyType == HistoryLocation.CLASSIFIED) {
            SpreadsheetService.getClassifiedSpreadsheet(authContext.login.user.id)
                .then((res: AxiosResponse) => { setSpreadsheetsData(res.data); })
                .catch((error: AxiosError) => { homeContext.showError('', error); });
        } else
            setLoading(false);

    }, [historyType]);

    const setSpreadsheetsData = (data: SpreadsheetModel[]) => {
        setSpreadsheets(data);
        setLoading(false);
    };

    const tableColumns: Column<SpreadsheetModel>[] = [
        getTableColumn('Origem da notícia', 'fileDescription', 'string', 'left', undefined, undefined, false, false),
        getTableColumn('Cliente', 'clientDescription', 'string', 'left', undefined, undefined, false),
        getTableColumn('Clipadora', 'clipperDescription', 'string', 'left', undefined, undefined, false),
        {
            title: 'Data de inserção',
            field: 'creationDate',
            type: 'datetime',
            align: 'center',
            filtering: false,
            dateSetting: { locale: 'pt-BR' },
            defaultSort: 'desc',
            cellStyle: { minWidth: '190px' }
        }
    ];

    const pendingTableColumns: Column<SpreadsheetModel>[] = [
        {
            title: 'Notícias pendentes',
            render: (rowData: SpreadsheetModel) => `${rowData.newsByStatus} de ${rowData.totalNews}`,
            type: 'string',
            align: 'center',
            filtering: false,
            customSort: (a: SpreadsheetModel, b: SpreadsheetModel) => a.newsByStatus - b.newsByStatus,
            cellStyle: { minWidth: '200px' }
        }
    ];

    const classifiedTableOptions: Options<SpreadsheetModel> = {
        filtering: true,
        toolbar: true,
        showTitle: false,
        pageSizeOptions: [5, 10],
    };

    const pendingTableOptions: Options<SpreadsheetModel> = {
        filtering: true,
        actionsColumnIndex: -1,
        pageSizeOptions: [5, 10],
    };

    const pendingTableToolbarText: ITableCommonToolbar = {
        title: 'Qual classificação gostaria de realizar?',
        subtitle: 'Selecione um lote de notícias para classificar ou realize o upload de uma nova planilha de notícias.'
    };

    const pendingTableActions: (Action<any> | ((rowData: any) => Action<any>))[] = [
        {
            icon: 'edit',
            iconProps: { style: { fontSize: '20px' } },
            tooltip: 'Classificar',
            onClick: (_, rowData: SpreadsheetModel) => {
                homeContext.setSpreadsheetId(rowData.id);
                homeContext.setPageLocation(PageLocation.CLASSIFICATION);
            }
        },
        rowData => ({
            icon: 'delete_outline',
            iconProps: { style: { fontSize: '20px' } },
            tooltip: 'Excluir lote de notícias',
            onClick: (_, rowData: SpreadsheetModel) => {
                setSpreadsheetDelete(rowData);
                setShowConfirmDelete(true);
            },
            disabled: rowData.newsByStatus != rowData.totalNews,
            hidden: rowData.newsByStatus != rowData.totalNews
        }),
        rowData => ({
            icon: 'list_alt_outlined',
            iconProps: { style: { fontSize: '20px' } },
            tooltip: 'Ver IDs gerados',
            onClick: (_, rowData: SpreadsheetModel) => {
                homeContext.setSpreadsheetId(rowData.id);
                homeContext.setPageLocation(PageLocation.NEWS_METRIC_IDS);
            },
            disabled: rowData.newsByStatus == rowData.totalNews && rowData.totalNews != 0,
            hidden: rowData.newsByStatus == rowData.totalNews && rowData.totalNews != 0
        })
    ];

    const classifiedTableActions: Action<any>[] = [
        {
            icon: 'list_alt_outlined',
            iconProps: { style: { fontSize: '20px' } },
            tooltip: 'Ver IDs gerados',
            onClick: (_, rowData: SpreadsheetModel) => {
                homeContext.setSpreadsheetId(rowData.id);
                homeContext.setPageLocation(PageLocation.NEWS_METRIC_IDS);
            }
        }
    ];

    const deleteSpreadsheet = () => {
        setShowConfirmDelete(false);
        setLoading(true);
        SpreadsheetService.spreadsheetService(authContext.login.user.id)
            .remove(spreadsheetDelete.id)
            .then(() => { window.location.reload(); })
            .catch((error: AxiosError) => { setLoading(false); homeContext.showError('', error); });

    };

    const getTabs = () => {
        return (
            <Tabs
                classes={{ flexContainer: classes.tabs }}
                value={historyType}
                indicatorColor="primary"
                textColor="primary"
                onChange={(_, newValue: HistoryLocation) => setHistoryType(newValue)}>
                <Tab value={HistoryLocation.PENDING} label='Notícias pendentes' />
                <Tab value={HistoryLocation.CLASSIFIED} label='Notícias classificadas' />
            </Tabs>
        );
    };

    const getTitleAndSubtitle = () => {
        return (
            <>
                <Typography variant="h4" className={classes.title}>
                    Histórico de classificações
                </Typography>
                {getTabs()}
            </>);
    };

    const getAddNewButton = () => {
        return (
            <Grid item className={classes.addNewButtonGrid}>
                <Button
                    onClick={() => homeContext.setPageLocation(PageLocation.NEWS_UPLOAD)}
                    size='large'
                    variant='contained'
                    color='primary'>
                    INSERIR NOVA PLANILHA
                </Button>
            </Grid>
        );
    };

    const getEmptyTable = (historyLocation: HistoryLocation, displayText: string) => {
        if (historyType == historyLocation && spreadsheets.length == 0 && !loading) {
            return (
                <Grid
                    container
                    spacing={3}
                    direction="column"
                    alignItems="center">
                    <Grid item className={classes.emptyTable}>
                        <TableEmpty displayText={displayText} />
                    </Grid>

                    {getAddNewButton()}
                </Grid>
            );
        }
    };

    return (
        <>
            <Grid container justify="center">
                <Grid
                    container
                    className={classes.root}
                    justify="flex-start"
                    direction="column">
                    {getTitleAndSubtitle()}

                    {historyType == HistoryLocation.PENDING && spreadsheets.length > 0 && !loading && (
                        <TableCommon
                            tableData={spreadsheets}
                            tableColumns={tableColumns.concat(pendingTableColumns)}
                            emptyDataSourceMessage='Nenhuma notícia encontrada para esse filtro.'
                            tableActions={pendingTableActions}
                            extraOptions={pendingTableOptions}
                            toolbar={pendingTableToolbarText}
                            addNewButtonText='Inserir nova planilha'
                            addNewButtonPageLocation={PageLocation.NEWS_UPLOAD}
                            style={{ padding: theme.spacing(0, 11) }} />
                    )}
                    {historyType == HistoryLocation.CLASSIFIED && spreadsheets.length > 0 && !loading && (
                        <TableCommon
                            tableData={spreadsheets}
                            tableColumns={tableColumns}
                            emptyDataSourceMessage='Nenhuma notícia encontrada para esse filtro.'
                            tableActions={classifiedTableActions}
                            extraOptions={classifiedTableOptions}
                            style={{ padding: theme.spacing(0, 11) }} />
                    )}

                    {loading &&
                        <TableLoading displayText="Carregando histórico de classificações" />}

                    {getEmptyTable(HistoryLocation.PENDING, 'Não existem notícias pendentes')}
                    {getEmptyTable(HistoryLocation.CLASSIFIED, 'Não existem notícias classificadas')}
                </Grid>
            </Grid>
            <BasicConfirmDialog
                title={'Tem certeza que deseja excluir?'}
                description={
                    spreadsheetDelete && spreadsheetDelete.newsOrigin == NewsOrigin.SpreadsheetImport
                        ? 'Ao excluir esta planilha, você não poderá classificar as notícias que ela contém.'
                        : 'Ao excluir este lote de notícias, você não poderá classificar as notícias que estão dentro dele e ' +
                        'não será possível obtê-lo novamente de maneira automática.'}
                handleFirstButton={deleteSpreadsheet}
                handleSecondButton={() => setShowConfirmDelete(false)}
                textFirstButton={'Excluir'}
                textSecondButton={'Cancelar'}
                open={showConfirmDelete} />
        </>
    );
}